import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { cn } from '@mds/shared/lib/utils';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Modal } from '@mds/shared/ui/feedback/modal';
import { Checkbox } from '@mds/shared/ui/shadcn/checkbox';

import type { Policy, PolicyLanguage } from '@/data';

/** The dialog's own words, in each language the policy is offered in. */
const COPY: Record<
  PolicyLanguage,
  { close: string; accept: string; pdf: string; readThen: string }
> = {
  en: {
    close: 'Close',
    accept: 'Accept',
    pdf: 'Download PDF',
    readThen: 'Read the policy, then accept it to continue.',
  },
  ar: {
    close: 'إغلاق',
    accept: 'أوافق',
    pdf: 'تنزيل PDF',
    readThen: 'اقرأ السياسة ثم وافق عليها للمتابعة.',
  },
};

/** How close to the bottom counts as the end, so a fraction of a pixel short still does. */
const END_SLACK = 8;

const LANGUAGES = [
  { value: 'en', label: 'English', lang: 'en' },
  { value: 'ar', label: 'العربية', lang: 'ar' },
] as const;

export type PolicyConsentProps = {
  policy: Policy;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Set by the page when it is sent without the box ticked. */
  error?: string;
  className?: string;
};

/**
 * "I have read and accept the booking policy", one line on the page.
 *
 * The policy itself opens in a dialog rather than a new page or a PDF, so the
 * visitor reads it and is back on the booking in one step. Accept ticks the box
 * and closes; Close, Escape or a click outside closes without ticking. Ticking
 * the box directly works too: reading is offered, not forced.
 *
 * Accept stays off until the text has been scrolled to its end, once, in
 * either language. A policy short enough to fit
 * without scrolling is read as soon as it opens.
 *
 * The dialog reads in English or Arabic, switched by a link in its footer. Arabic turns the
 * whole dialog right to left and onto the Arabic face; the line on the page
 * stays in the page's language.
 */
export default function PolicyConsent({
  policy,
  checked,
  onCheckedChange,
  error,
  className,
}: PolicyConsentProps) {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState<PolicyLanguage>('en');
  const boxId = useId();
  const errorId = useId();

  const [readToEnd, setReadToEnd] = useState(false);
  const scrollBox = useRef<HTMLDivElement>(null);

  const text = policy.text[language];
  const copy = COPY[language];
  const isArabic = language === 'ar';
  /** The language the switch offers: whichever one is not showing. */
  const other = LANGUAGES.find((l) => l.value !== language)!;

  const checkEnd = useCallback(() => {
    const box = scrollBox.current;
    if (box && box.scrollTop + box.clientHeight >= box.scrollHeight - END_SLACK) {
      setReadToEnd(true);
    }
  }, []);

  // On opening and on a language switch the text may already fit, with
  // nothing to scroll. Checked a frame later, once the dialog has laid out.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(checkEnd);
    return () => cancelAnimationFrame(frame);
  }, [open, language, checkEnd]);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-start gap-2.5 text-sm text-ink-secondary">
        <Checkbox
          id={boxId}
          checked={checked}
          onCheckedChange={(state) => onCheckedChange(state === true)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className="mt-0.5"
        />
        {/* The link sits inside the label: a click on it opens the dialog
            and does not tick the box, since a label ignores clicks that
            land on a control of its own. */}
        <label htmlFor={boxId}>
          {/* No full stop: a checkbox label is a phrase, and without one the
              line fits the booking card beside the map. */}
          I have read and accept the{' '}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-brand-text underline underline-offset-3 hover:no-underline"
          >
            {policy.text.en.title.toLowerCase()}
          </button>
        </label>
      </div>

      {error && (
        <p id={errorId} className="ml-6.5 text-sm text-danger-text">
          {error}
        </p>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={text.title}
        description={copy.readThen}
        dir={isArabic ? 'rtl' : 'ltr'}
        lang={language}
        bodyRef={scrollBox}
        onBodyScroll={readToEnd ? undefined : checkEnd}
        bodyClassName="flex flex-col gap-5 text-sm text-ink-secondary"
        footer={
          <>
            {/* The language switch is one link, named in the other language,
                down here with the PDF rather than in the header: the title
                keeps its line, with only the × beside it. */}
            <div className="flex items-center justify-center gap-4 sm:me-auto">
              <button
                type="button"
                lang={other.lang}
                onClick={() => setLanguage(other.value)}
                className="text-sm text-brand-text underline underline-offset-3 hover:no-underline"
              >
                {other.label}
              </button>
              {policy.pdfUrl && (
                <a
                  href={policy.pdfUrl}
                  download
                  className="text-sm text-brand-text underline underline-offset-3 hover:no-underline"
                >
                  {copy.pdf}
                </a>
              )}
            </div>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {copy.close}
            </Button>
            <Button
              disabled={!readToEnd}
              onClick={() => {
                onCheckedChange(true);
                setOpen(false);
              }}
            >
              {copy.accept}
            </Button>
          </>
        }
      >
        {text.intro?.map((paragraph, i) => (
          <p key={i} className="m-0">
            {paragraph}
          </p>
        ))}
        {text.sections.map((section) => (
          <section key={section.heading} className="flex flex-col gap-1.5">
            {/* A heading is on the display face by default; the body face
                    reads better at this size, and Arabic needs its own. */}
            <h3
              className={cn('text-sm font-medium text-ink', isArabic ? 'font-arabic' : 'font-sans')}
            >
              {section.heading}
            </h3>
            {section.body.map((paragraph, i) => (
              <p key={i} className="m-0">
                {paragraph}
              </p>
            ))}
            {section.points && (
              // `ps-5` and `list-disc` follow the dialog's direction, so the
              // bullets sit on the right in Arabic.
              <ul className="m-0 flex list-disc flex-col gap-1 ps-5">
                {section.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </Modal>
    </div>
  );
}
